import FileExchangePanel from '@/components/FileExchangePanel';
import { useI18n } from '@/i18n/I18nContext';
import { Text, Skeleton, Stack, useDisclosure } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { amountCell, dateCell } from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { Commitment } from 'src/api/Commitment';
import Content from 'src/components/Content';
import { commitmentService } from "src/service/CommitmentService";
import IdAndNameData from 'src/data/IdAndNameData';
import ConfirmDialog from 'src/components/ConfirmDialog';
import ReachLink from 'src/components/ReachLink';
import AddButton from 'src/components/buttons/AddButton';
import { FileDownload, FileSelect } from 'src/components/FileSelect';
import { formatPicker } from 'src/data/DateTimeUtil';
import WaitingSpinner from '@/components/WaitingSpinner';
import { DropDownMenu } from '@/components/DropDownMenu';
import { toaster } from '@/components/ui/toaster';


const OverviewCommitments = () => {
    const { t } = useI18n();
    const [data, setData] = useState<Commitment[]>([]);
    const [projects, setProjects] = useState<Map<number, string>>(new Map()); // id to project name
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { open, onOpen, onClose } = useDisclosure(); // for delete dialog

    const navigate = useNavigate();

    const onDelete = useCallback((id: number, name: string): Promise<void> => {
        console.info(`deleting ${id} ${name}`);
        return commitmentService.remove(id)
            .then(() => {
                setData(prev => prev.filter(item => item.id !== id));
                setDeleteData(null);
                onClose();
                toaster.create({
                    title: 'Zusage gelöscht',
                    type: 'success'
                });
            });
    }, [onClose]);

    const onEdit = useCallback((id: number): void => {
        console.info(`editing ${id}`);
        navigate(`/fundraising/commitments/edit/${id}`);
    }, [navigate]);

    const onView = useCallback((id: number): void => {
        console.info(`viewing ${id}`);
        navigate(`/fundraising/commitments/view/${id}`);
    }, [navigate]);

    const loadAllData = (): Promise<void> => {
        setIsLoading(true);
        return commitmentService.listAll().then(r => {
            setData(r.results.concat());
            setProjects(new Map(r.projects.map(p => [p.id, p.name])));
        })
            .finally(() => setIsLoading(false));
    }

    const onUploadFinished = useCallback((): Promise<void> => {
        console.info(`upload commitments suceeded...`);
        return loadAllData();
    }, []);

    useEffect(() => {
        loadAllData();
    }, [onUploadFinished]);

    const columns: ColumnDef<Commitment>[] = useMemo(
        () => [
            {
                id: 'year',
                header: 'Jahr',
                accessorFn: (row) => {
                    const year = new Date(row.entryDate).getFullYear();
                    return Number.isNaN(year) ? '' : year;
                },
                cell: (info: CellContext<Commitment, number>) => (<Text fontWeight={'extrabold'}>{info.getValue()}</Text>),
            },
            {
                id: 'entryDate',
                header: 'Datum',
                accessorKey: 'entryDate',
                cell: dateCell(),
                enableGrouping: false,
            },
            {
                id: 'project',
                header: 'Projekt',
                accessorKey: 'projectId',
                cell: (info: CellContext<Commitment, number>) => {
                    let pid = info.getValue() ?? 0;
                    let projectName = projects.get(pid);
                    return (<ReachLink to={`/projects/view/${pid}`} name={projectName} tooltip='zu den Projektdetails' />);
                },
                sortFn: (rowA: Row<Commitment>, rowB: Row<Commitment>, _): number => {
                    const a = rowA.original;
                    const b = rowB.original;
                    let ap = projects.get(a.projectId) ?? '';
                    let bp = projects.get(b.projectId) ?? '';
                    return ap.trim().localeCompare(bp.trim(), undefined, { sensitivity: 'base' });
                },
                aggregatedCell: '',
                enableGrouping: false,
            },
            {
                id: 'fundSourceName',
                header: 'Fonds Quelle (Institution)',
                accessorKey: 'fundSourceName',
                enableGrouping: false,
            },
            {
                id: 'amount',
                header: () => (<div style={{ textAlign: 'right', width: '10rem' }}>Summe</div>),
                accessorKey: 'amount',
                cell: amountCell({}),
                aggregatedCell: amountCell({ fontWeight: 'bold' }),
                enableGrouping: false,
            },
            {
                id: 'comments',
                header: () => (<div style={{ textAlign: 'right', width: '20rem' }}>Kommentar</div>),
                accessorKey: 'comments',
                enableGrouping: false,
            },
            {
                id: 'edit',
                enableSorting: false,
                enableGrouping: false,
                cell: (info) => {
                    let row = info.row.original;
                    return (
                        <DropDownMenu onMenuView={() => onView(row.id)} onMenuEdit={() => onEdit(row.id)} onMenuDelete={() => {

                            let da: IdAndNameData<number> = {
                                id: row.id,
                                name: `${projects.get(row.projectId)} / ${row.fundSourceName ?? 'n/a'}`
                            };
                            setDeleteData(da);
                            onOpen();
                        }} />
                    )
                }
            }
        ], [projects, onEdit, onOpen, onView]
    );

    return (
        <Content
            title={t('overviews.commitments.title')}
            description={t('overviews.commitments.description')}
            actions={<>
                <FileExchangePanel>
                    <FileSelect path='/api/fundraising/commitments/upload' onUpload={onUploadFinished} />
                    <FileDownload scope='all' path='/api/fundraising/commitments/download' name={`zusagen-${formatPicker(new Date())}.xlsx`} />
                </FileExchangePanel>
                <AddButton caption={t('overviews.commitments.create')} onClick={() => navigate('/fundraising/commitments/add')} />
            </>}
        >
            <Stack gap='6'>
                {isLoading ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : (
                    <>
                        <Text fontSize='sm' color='fg.muted' textAlign='end' aria-live='polite'>{t('common.table.results', { count: data.length })}</Text>
                        <DataTable columns={columns} data={data}
                            initialSortByColumn={['year', 'entryDate']} initialSortByDesc={[false, true]} hideColumnsOnXs={['project', 'comments']}
                            initialGroupByColumns={['year']}
                            onRowClick={row => onEdit(row.id)}
                        />
                        <ConfirmDialog
                            heading={`${deleteData?.name} Löschen`}
                            isOpen={open}
                            onClose={onClose}
                            onConfirm={async (): Promise<void> => onDelete(deleteData.id, deleteData.name)}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );
}

export default OverviewCommitments
